import { Link, useParams } from "react-router-dom";
import styled from "styled-components";
import NewFooter from "../../components/Footers/NewFooter";
import NewNavbar from "../../components/Navbar/NewNavbar";
import RichText from "../../components/RichText/RichText";
import Seo, { SITE_URL } from "../../components/Seo/Seo";
import { blocksToPlainText } from "../../content/richText";
import { getTool } from "../../content/tools";
import NotFoundPage from "../NotFoundPage/NotFoundPage";

const Page = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;
const Hero = styled.section`
  padding: 120px 0 24px;
  background: #fff;
  text-align: center;
`;
const Wrap = styled.div`
  max-width: 760px;
  margin: 0 auto;
  padding: 0 24px;
`;
const Title = styled.h1`
  font-size: 44px;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 16px;
  @media (max-width: 767px) {
    font-size: 32px;
  }
`;
const Sub = styled.p`
  font-size: 20px;
  color: #4a4a4a;
  line-height: 1.6;
  margin: 0 auto;
  max-width: 620px;
`;
const WidgetCard = styled.div`
  background: #f9fafb;
  border: 1px dashed #d1d5db;
  border-radius: 16px;
  padding: 40px 32px;
  text-align: center;
  margin: 40px auto 0;
`;
const Badge = styled.span`
  display: inline-block;
  background: #eef2ff;
  color: #4f46e5;
  font-size: 13px;
  font-weight: 700;
  padding: 4px 12px;
  border-radius: 999px;
  margin-bottom: 16px;
`;
const Button = styled(Link)`
  display: inline-block;
  background: var(--brand-primary, #0e7e7a);
  color: #fff;
  padding: 14px 32px;
  border-radius: 50px;
  font-weight: 700;
  text-decoration: none;
  margin-top: 8px;
  &:hover {
    opacity: 0.9;
    text-decoration: none;
  }
`;
const Body = styled.section`
  background: #f9fafb;
  padding: 32px 0 64px;
  flex: 1;
  font-size: 17px;
  line-height: 1.7;
  color: #2a2a2a;

  h2 {
    font-size: 26px;
    font-weight: 700;
    color: #1a1a1a;
    margin: 36px 0 12px;
    scroll-margin-top: 100px;
  }
  h3 {
    font-size: 20px;
    font-weight: 700;
    margin: 24px 0 8px;
  }
  p {
    margin: 0 0 16px;
  }
  ul,
  ol {
    margin: 0 0 16px;
    padding-left: 24px;
  }
  li {
    margin-bottom: 8px;
  }
  a {
    color: #6b7280;
    font-weight: 600;
  }
`;
const FaqQ = styled.h3`
  scroll-margin-top: 100px;
`;

/**
 * @param {import("../../content/tools").ToolContent} tool
 * @returns {Record<string, unknown>[]}
 */
const toolJsonLd = (tool) => {
  const url = `${SITE_URL}/tools/${tool.slug}`;
  const blocks = /** @type {Record<string, unknown>[]} */ ([
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: tool.name,
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      url,
      description: tool.description,
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
      publisher: { "@type": "Organization", name: "Listino", url: `${SITE_URL}/` },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: tool.name, item: url },
      ],
    },
  ]);
  if (tool.faqs?.length) {
    blocks.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: tool.faqs.map((f) => ({
        "@type": "Question",
        name: f.question,
        acceptedAnswer: { "@type": "Answer", text: blocksToPlainText(f.answer) },
      })),
    });
  }
  return blocks;
};

const ToolPage = () => {
  const { slug } = /** @type {{slug: string}} */ (useParams());
  const tool = getTool(slug);
  if (!tool) return <NotFoundPage />;

  return (
    <Page>
      <Seo
        title={tool.metaTitle || `${tool.title} | Listino`}
        description={tool.description}
        path={`/tools/${tool.slug}`}
        jsonLd={toolJsonLd(tool)}
      />
      <NewNavbar />

      <Hero>
        <Wrap>
          <Title>{tool.title}</Title>
          <Sub>{tool.tldr}</Sub>
          <WidgetCard>
            <Badge>{tool.status === "live" ? "Free tool" : "Free tool — coming soon"}</Badge>
            <p>
              {tool.status === "live"
                ? "Use the tool below."
                : "The interactive tool is on the way. In the meantime, get the full, expert version in a Listino listing prep."}
            </p>
            <Button to={tool.primaryCtaPath || "/get-started"}>
              {tool.status === "live" ? "Open the tool" : "Get your listing prep"}
            </Button>
          </WidgetCard>
        </Wrap>
      </Hero>

      <Body>
        <Wrap>
          <RichText blocks={tool.blocks} />

          {tool.faqs && tool.faqs.length > 0 && (
            <>
              <h2>Frequently asked questions</h2>
              {tool.faqs.map((faq) => (
                <div key={faq.question}>
                  <FaqQ>{faq.question}</FaqQ>
                  <RichText blocks={faq.answer} />
                </div>
              ))}
            </>
          )}
        </Wrap>
      </Body>

      <NewFooter />
    </Page>
  );
};

export default ToolPage;
